/**
 * Camera utility functions for automatic bounds detection
 */

/**
 * Automatically set camera bounds based on tilemap dimensions
 * @param camera - Phaser camera instance
 * @param tilemap - Phaser tilemap instance
 * @param padding - Optional padding around map edges (default: 0)
 */
export function setAutomaticCameraBounds(
  camera: Phaser.Cameras.Scene2D.Camera,
  tilemap: Phaser.Tilemaps.Tilemap,
  padding: number = 0
): void {
  if (!camera) {
    console.warn(
      "[CameraUtils] Cannot set camera bounds: camera is null or undefined"
    );
    return;
  }

  if (!tilemap) {
    console.warn(
      "[CameraUtils] Cannot set camera bounds: tilemap is null or undefined"
    );
    return;
  }

  // Get map dimensions in tiles
  const mapWidth = tilemap.width;
  const mapHeight = tilemap.height;
  const tileWidth = tilemap.tileWidth;
  const tileHeight = tilemap.tileHeight;

  // Validate tilemap data
  if (!mapWidth || !mapHeight || !tileWidth || !tileHeight) {
    console.warn("[CameraUtils] Invalid tilemap dimensions:", {
      mapWidth,
      mapHeight,
      tileWidth,
      tileHeight,
    });
    return;
  }

  // Calculate world dimensions in pixels
  const worldWidth = mapWidth * tileWidth;
  const worldHeight = mapHeight * tileHeight;

  // Apply padding
  const x = -padding;
  const y = -padding;
  const width = worldWidth + padding * 2;
  const height = worldHeight + padding * 2;

  // Set camera bounds
  camera.setBounds(x, y, width, height);
}

/**
 * Get tilemap world dimensions in pixels
 * @param tilemap - Phaser tilemap instance
 * @returns Object with width and height in pixels, or null if tilemap is invalid
 */
export function getTilemapWorldSize(
  tilemap: Phaser.Tilemaps.Tilemap
): { width: number; height: number } | null {
  if (!tilemap) {
    return null;
  }

  return {
    width: tilemap.width * tilemap.tileWidth,
    height: tilemap.height * tilemap.tileHeight,
  };
}

/**
 * Get tilemap dimensions info for debugging
 * @param tilemap - Phaser tilemap instance
 * @returns Formatted string with tilemap dimension info
 */
export function getTilemapInfo(tilemap: Phaser.Tilemaps.Tilemap): string {
  if (!tilemap) {
    return "Tilemap is null or undefined";
  }

  const worldSize = getTilemapWorldSize(tilemap);
  return `Map: ${tilemap.width}x${tilemap.height} tiles (${tilemap.tileWidth}x${tilemap.tileHeight}px each) = ${worldSize?.width}x${worldSize?.height}px world`;
}

/**
 * Set camera bounds with automatic detection and fallback
 * @param camera - Phaser camera instance
 * @param tilemap - Phaser tilemap instance
 * @param fallbackBounds - Fallback bounds if automatic detection fails {x, y, width, height}
 * @param padding - Optional padding around map edges (default: 0)
 */
export function setSmartCameraBounds(
  camera: Phaser.Cameras.Scene2D.Camera,
  tilemap: Phaser.Tilemaps.Tilemap | null,
  fallbackBounds: { x: number; y: number; width: number; height: number },
  padding: number = 0
): void {
  if (!camera) {
    console.warn(
      "[CameraUtils] Cannot set camera bounds: camera is null or undefined"
    );
    return;
  }

  // Try automatic detection first
  if (
    tilemap &&
    tilemap.width &&
    tilemap.height &&
    tilemap.tileWidth &&
    tilemap.tileHeight
  ) {
    setAutomaticCameraBounds(camera, tilemap, padding);
  } else {
    // Use fallback bounds
    console.warn(
      "[CameraUtils] Using fallback camera bounds due to invalid tilemap"
    );
    camera.setBounds(
      fallbackBounds.x,
      fallbackBounds.y,
      fallbackBounds.width,
      fallbackBounds.height
    );
  }
}

/**
 * Handle resize event to maintain camera zoom at 1
 * This prevents the game content from appearing smaller when the browser window is resized
 * @param scene - Phaser scene instance
 * @param gameSize - Game size from resize event (optional, not used but matches Phaser's callback signature)
 */
export function handleSceneResize(
  scene: Phaser.Scene,
  gameSize?: Phaser.Structs.Size
): void {
  if (scene.cameras && scene.cameras.main) {
    scene.cameras.main.setZoom(1);
  }
}

/**
 * Calculate zoom level based on device type and pixel ratio
 * Higher resolution devices (higher pixel ratio) get increased zoom to show more detail
 * @param baseZoom - Base zoom level for the device type (phone: 1.5, tablet: 1.3, desktop: 1.1)
 * @param gameWidth - Game canvas width (not used currently)
 * @param gameHeight - Game canvas height (not used currently)
 * @returns Zoom level adjusted for device pixel ratio
 */
export function calculateAspectRatioZoom(
  baseZoom: number = 1.1,
  gameWidth: number = 560,
  gameHeight: number = 405
): number {
  // Get device pixel ratio (1 on normal screens, 2+ on high-DPI screens)
  const pixelRatio = window.devicePixelRatio || 1;

  // Increase zoom proportionally with pixel ratio
  // On 1x screens: zoom = baseZoom
  // On 2x screens: zoom = baseZoom * 1.5
  // On 3x screens: zoom = baseZoom * 2.0
  // This keeps the game world visible while making UI and details sharper on high-res devices
  const pixelRatioZoom = Math.max(1, pixelRatio * 0.7 + 0.5); // Dampening factor so it's not too extreme

  return baseZoom * pixelRatioZoom;
}
